import type React from 'react';
import classNames from 'classnames';

import { Card, CardHeader, type CardPadding } from '@/components/atoms/Card';
import { ChartTitleProvider } from '@/components/charts/ChartTitleContext';

import styles from './ContentBlock.module.scss';

export type ContentBlockLayout = 'default' | 'grid-2-1' | 'grid-2' | 'grid-3' | 'flex';
export type ContentBlockSize = 'default' | 'large' | 'fit-content' | 'compact';
export type ContentBlockSpacing = 'none' | 'default' | 'top' | 'bottom' | 'top-and-bottom';

interface ContentBlockProps {
    title?: string;
    /** Renders the title as this heading, for a block that names a section of the page. */
    titleLevel?: 'h2' | 'h3' | 'h4';
    /** The title's type size; see `CardHeader`'s `size`. */
    titleSize?: 'card' | 'section';
    subtitle?: string;
    headerActions?: React.ReactNode;
    titleIcon?: React.ReactNode;
    contentLayout?: ContentBlockLayout;
    customContentLayout?: string;
    size?: ContentBlockSize;
    spacing?: ContentBlockSpacing;
    /**
     * Lets the block narrow past its 320px floor, for a block in a narrow column or a grid
     * track that sizes it.
     */
    shrink?: boolean;
    /**
     * Stretches the content to the block's height, which the caller sets with `className`:
     * the content takes what the header leaves, for a pair of blocks held level or an image
     * that scales to its card.
     */
    fill?: boolean;
    className?: string;
    /** Optional: a card may be header-only, as the images page's caption block is. */
    children?: React.ReactNode;
    containerRef?: React.RefObject<HTMLDivElement | null>;
    /** Makes the title a focus target (tabIndex -1), for moving focus to the card. */
    titleRef?: React.Ref<HTMLElement>;
    /** Extra class on the header row, for a card whose actions need their own layout. */
    headerClassName?: string;
}

const PADDING_BY_SIZE: Record<ContentBlockSize, CardPadding> = {
    default: 'lg',
    compact: 'md',
    large: 'xl',
    'fit-content': 'none',
};

export default function ContentBlock(props: ContentBlockProps) {
    const {
        title,
        titleLevel,
        titleSize,
        subtitle,
        headerActions,
        titleIcon,
        contentLayout = 'default',
        customContentLayout,
        size = 'default',
        spacing = 'default',
        shrink = false,
        fill = false,
        className,
        children,
        containerRef,
        titleRef,
        headerClassName,
    } = props;
    const hasHeader = title || subtitle || headerActions || titleIcon;

    return (
        <Card
            containerRef={containerRef}
            padding={PADDING_BY_SIZE[size]}
            fullWidth={false}
            className={classNames(
                styles.contentBlock,
                styles[`spacing-${spacing}`],
                { [styles.shrink]: shrink, [styles.fill]: fill },
                className
            )}
            header={
                hasHeader ? (
                    <CardHeader
                        title={title || undefined}
                        level={titleLevel}
                        size={titleSize}
                        subtitle={subtitle || undefined}
                        icon={titleIcon || undefined}
                        actions={headerActions || undefined}
                        titleRef={titleRef}
                        className={headerClassName}
                    />
                ) : undefined
            }>
            <div
                className={classNames(
                    styles.content,
                    styles[`layout-${contentLayout}`],
                    { [styles.contentFill]: fill },
                    customContentLayout
                )}>
                {/* A chart in the block is named after the block's title. */}
                <ChartTitleProvider title={title}>{children}</ChartTitleProvider>
            </div>
        </Card>
    );
}
